import axios from 'axios';
import { supabase } from '../lib/supabase';
import type {
  ScreenshotUploadResponse,
  IPISearchResponse,
  IPISearchCategorizedResponse,
  WriterLookupResponse,
  RegistrationResult,
  SongData,
  Writer,
  WriterSplit,
  SpotifyTrackResponse,
  HybridComponent,
  StateMutation,
} from '../types';

// API base URL - use VITE_API_URL in production, localhost in development
const API_BASE_URL = import.meta.env.DEV
  ? 'http://localhost:5001/api'
  : (import.meta.env.VITE_API_URL as string) || '/api';

// --- Auth token cache ---
let cachedToken: string | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  cachedToken = session?.access_token ?? null;
});

// Seed the cache on module load
supabase.auth.getSession().then(({ data: { session } }) => {
  cachedToken = session?.access_token ?? null;
});

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach cached auth token (synchronous — no async getSession per request)
api.interceptors.request.use((config) => {
  if (cachedToken) {
    config.headers.Authorization = `Bearer ${cachedToken}`;
  }
  return config;
});

// --- IPI search deduplication cache ---
const ipiCache = new Map<string, { data: IPISearchCategorizedResponse; expires: number }>();
const IPI_CACHE_TTL = 60_000; // 60 seconds

function getCachedIPI(key: string): IPISearchCategorizedResponse | null {
  const entry = ipiCache.get(key);
  if (entry && Date.now() < entry.expires) {
    return entry.data;
  }
  ipiCache.delete(key);
  return null;
}

// --- AbortController tracking ---
const activeControllers = new Map<string, AbortController>();

function getAbortSignal(key: string): AbortSignal {
  // Cancel any previous request with the same key
  activeControllers.get(key)?.abort();
  const controller = new AbortController();
  activeControllers.set(key, controller);
  return controller.signal;
}

function clearAbortController(key: string) {
  activeControllers.delete(key);
}

// --- Retry helper ---
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 2,
  baseDelay = 1000
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      // Only retry on network errors or 5xx responses
      const isRetryable =
        axios.isAxiosError(error) &&
        (!error.response || error.response.status >= 500);
      if (!isRetryable || attempt === maxRetries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, baseDelay * 2 ** attempt));
    }
  }
  throw lastError;
}

// Health check
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await api.get('/health');
    return response.data.status === 'healthy';
  } catch {
    return false;
  }
}

// Upload screenshot for processing
export async function uploadScreenshot(imageFile: File): Promise<ScreenshotUploadResponse> {
  const formData = new FormData();
  formData.append('image', imageFile);

  const response = await api.post<ScreenshotUploadResponse>('/upload/screenshot', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

// Search IPI database for a single name (legacy)
export async function searchIPI(name: string, limit = 3): Promise<IPISearchResponse> {
  const signal = getAbortSignal(`ipi-${name}`);
  try {
    const response = await api.get<IPISearchResponse>('/ipi/search', {
      params: { name, limit },
      signal,
    });
    return response.data;
  } finally {
    clearAbortController(`ipi-${name}`);
  }
}

// Search IPI database with categorized results (close matches + potential/variant matches)
export async function searchIPICategorized(name: string, limit = 5): Promise<IPISearchCategorizedResponse> {
  const cacheKey = `${name}:${limit}`;

  // Return cached result if available
  const cached = getCachedIPI(cacheKey);
  if (cached) return cached;

  const signal = getAbortSignal(`ipi-cat-${name}`);
  try {
    const response = await api.get<IPISearchCategorizedResponse>('/ipi/search', {
      params: { name, limit },
      signal,
    });
    // Cache the result
    ipiCache.set(cacheKey, { data: response.data, expires: Date.now() + IPI_CACHE_TTL });
    return response.data;
  } finally {
    clearAbortController(`ipi-cat-${name}`);
  }
}

// Look up IPI for multiple writers
export async function lookupWriters(writerNames: string[]): Promise<WriterLookupResponse> {
  const response = await api.post<WriterLookupResponse>('/writers/lookup', {
    writers: writerNames,
  });
  return response.data;
}

// Fetch track info from Spotify URL
export async function fetchSpotifyTrack(url: string): Promise<SpotifyTrackResponse> {
  const signal = getAbortSignal('spotify-track');
  try {
    const response = await api.post<SpotifyTrackResponse>('/spotify/track', { url }, { signal });
    return response.data;
  } finally {
    clearAbortController('spotify-track');
  }
}

// Register work in Airtable (with retry for resilience)
export async function registerWork(
  songData: SongData,
  writers: Writer[],
  splits: WriterSplit[]
): Promise<RegistrationResult> {
  return withRetry(async () => {
    const response = await api.post<RegistrationResult>('/register', {
      songData: {
        title: songData.title,
        artist: songData.artist,
        isrc: songData.isrc,
        releaseDate: songData.releaseDate,
        duration: songData.duration,
      },
      writers: writers.map(w => ({
        name: w.name,
        ipi: w.ipi,
        role: w.role,
      })),
      splits: splits.map(s => ({
        writer: s.writerName,
        author: s.author,
        composer: s.composer,
      })),
    });

    return response.data;
  });
}

// Chat types
export interface ChatResponse {
  response: string;
  state: string;
  transcription?: string;
  writers_pending?: boolean;
  writer_lookup_id?: string;
}

// Chat API functions
export async function sendChatMessage(message: string): Promise<ChatResponse> {
  const response = await api.post<ChatResponse>('/chat', {
    type: 'text',
    message,
  });
  return response.data;
}

export async function sendChatImage(imageFile: File, caption?: string): Promise<ChatResponse> {
  const formData = new FormData();
  formData.append('type', 'image');
  formData.append('image', imageFile);
  if (caption) {
    formData.append('caption', caption);
  }
  const response = await api.post<ChatResponse>('/chat', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function sendChatAudio(audioBlob: Blob): Promise<ChatResponse> {
  const formData = new FormData();
  formData.append('type', 'audio');
  formData.append('audio', audioBlob, 'recording.webm');
  const response = await api.post<ChatResponse>('/chat', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

// --- Hybrid chat types & functions ---

export interface HybridChatResponse {
  message: string;
  state: string;
  components: HybridComponent[];
  mutations: StateMutation[] | null;
  transcription?: string;
  writers_pending?: boolean;
  writer_lookup_id?: string;
}

export async function sendHybridMessage(message: string): Promise<HybridChatResponse> {
  const response = await api.post<HybridChatResponse>('/hybrid-chat', {
    type: 'text',
    message,
  });
  return response.data;
}

export async function sendHybridImage(imageFile: File, caption?: string): Promise<HybridChatResponse> {
  const formData = new FormData();
  formData.append('type', 'image');
  formData.append('image', imageFile);
  if (caption) {
    formData.append('caption', caption);
  }
  const response = await api.post<HybridChatResponse>('/hybrid-chat', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function sendHybridAudio(audioBlob: Blob): Promise<HybridChatResponse> {
  const formData = new FormData();
  formData.append('type', 'audio');
  formData.append('audio', audioBlob, 'recording.webm');
  const response = await api.post<HybridChatResponse>('/hybrid-chat', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

// --- Hybrid card mutations ---

export type HybridMutation =
  | { action: 'updateTrack'; field: string; value: unknown }
  | { action: 'updateWriter'; target: string; field: string; value: unknown }
  | { action: 'updateSplit'; target: string; field: string; value: unknown }
  | { action: 'confirmStage'; stage: number }
  | {
      action: 'applyComparisonChoices';
      title: 'mine' | 'spotify';
      artist: 'mine' | 'spotify';
      writers: Array<{ rowId: string; source: 'mine' | 'spotify' }>;
      addExtras: string[];
    }
  | { action: 'discardComparison' }
  | { action: 'provideSpotifyLink'; spotifyUrl: string }
  | { action: 'dismissSpotifyPrompt' };

export interface HybridMutationResponse extends HybridChatResponse {
  mutation_summary: {
    applied: number;
    transitioned_to: string | null;
    error: string | null;
  };
}

export async function sendHybridMutations(
  mutations: HybridMutation[],
): Promise<HybridMutationResponse> {
  const response = await api.post<HybridMutationResponse>('/hybrid-mutations', {
    mutations,
  });
  return response.data;
}

// --- Writer lookup polling ---

export interface WriterStatusResponse {
  status: 'pending' | 'complete' | 'failed' | 'not_found';
  message?: string;
  state?: string;
  components?: HybridComponent[];
  mutations?: StateMutation[] | null;
  error?: string;
}

export async function getWriterStatus(requestId: string): Promise<WriterStatusResponse> {
  const response = await api.get<WriterStatusResponse>(`/writer-status/${requestId}`);
  return response.data;
}

export default api;
